import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { driverApi } from '../../config/driverApi';
import { User, Truck, Phone, Mail, Save, Lock } from 'lucide-react';

const DriverProfile = () => {
  const { driver, setDriver } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle_type: 'Motorcycle',
    vehicle_registration: '',
    emergency_contact: '',
    emergency_phone: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await driverApi.get('/auth.php?action=profile');
      if (res.data.success) {
        const data = res.data.data;
        setDriver(data);
        setFormData({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          vehicle_type: data.vehicle_type || 'Motorcycle',
          vehicle_registration: data.vehicle_registration || '',
          emergency_contact: data.emergency_contact || '',
          emergency_phone: data.emergency_phone || '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('file', file);

    setUploadingPhoto(true);
    try {
      const res = await driverApi.post('/upload.php', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data.success) {
        const updatedProfile = { ...formData, profile_photo: res.data.url };
        const saveRes = await driverApi.put('/auth.php?action=profile', updatedProfile);
        if (saveRes.data.success) {
          setDriver(saveRes.data.data);
          alert('Profile photo updated successfully');
        } else {
          alert(saveRes.data.message || 'Failed to update profile image path');
        }
      } else {
        alert(res.data.message || 'Upload failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error uploading photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('New passwords do not match');
      return;
    }

    setUpdatingPassword(true);
    try {
      const res = await driverApi.put('/auth.php?action=change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      if (res.data.success) {
        alert('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
      } else {
        alert(res.data.message || 'Failed to change password');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error changing password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const res = await driverApi.put('/auth.php?action=profile', {
        ...formData,
        profile_photo: driver?.profile_photo || '',
      });
      if (res.data.success) {
        setDriver(res.data.data);
        alert('Profile updated successfully');
      } else {
        alert(res.data.message || 'Failed to update profile');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid #f0f0f0',
          borderTopColor: '#D4AF37',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{
        fontFamily: 'Playfair Display, serif',
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#1A1A1A',
        marginBottom: '20px',
      }}>
        My Profile
      </h2>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #EAEDF1',
        marginBottom: '16px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
      }}>
        <div style={{
          width: '96px',
          height: '96px',
          borderRadius: '50%',
          backgroundColor: '#D4AF3720',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          overflow: 'hidden',
          border: '3px solid #D4AF37',
        }}>
          {driver?.profile_photo ? (
            <img src={driver.profile_photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <User size={40} color="#D4AF37" />
          )}
        </div>
        <label style={{
          display: 'inline-block',
          padding: '8px 16px',
          borderRadius: '8px',
          backgroundColor: '#1a1a1a',
          color: '#fff',
          fontSize: '0.82rem',
          fontWeight: 600,
          cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
        }}>
          {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
          <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploadingPhoto} style={{ display: 'none' }} />
        </label>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #EAEDF1',
          marginBottom: '16px',
        }}>
          <h4 style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#666',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <User size={16} />
            Personal Information
          </h4>

          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              <Mail size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              <Phone size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #EAEDF1',
          marginBottom: '16px',
        }}>
          <h4 style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#666',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Truck size={16} />
            Vehicle Information
          </h4>

          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              Vehicle Type
            </label>
            <select
              name="vehicle_type"
              value={formData.vehicle_type}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            >
              <option value="Motorcycle">Motorcycle</option>
              <option value="Car">Car</option>
              <option value="Bicycle">Bicycle</option>
              <option value="Van">Van</option>
              <option value="Truck">Truck</option>
            </select>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              Vehicle Registration
            </label>
            <input
              type="text"
              name="vehicle_registration"
              value={formData.vehicle_registration}
              onChange={handleChange}
              placeholder="e.g., UG 1234 AB"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #EAEDF1',
          marginBottom: '20px',
        }}>
          <h4 style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#666',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Emergency Contact
          </h4>

          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              Contact Name
            </label>
            <input
              type="text"
              name="emergency_contact"
              value={formData.emergency_contact}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              Contact Phone
            </label>
            <input
              type="tel"
              name="emergency_phone"
              value={formData.emergency_phone}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '10px',
            backgroundColor: saving ? '#ccc' : '#D4AF37',
            color: '#fff',
            border: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #EAEDF1',
        marginTop: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
      }}>
        <h4 style={{
          fontSize: '0.85rem',
          fontWeight: 600,
          color: '#666',
          marginBottom: '16px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Lock size={16} />
          Change Password
        </h4>

        <form onSubmit={handlePasswordSubmit}>
          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              Current Password
            </label>
            <input
              type="password"
              required
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              New Password
            </label>
            <input
              type="password"
              required
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              required
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={updatingPassword}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: updatingPassword ? '#ccc' : '#1a1a1a',
              color: '#fff',
              border: 'none',
              fontSize: '0.88rem',
              fontWeight: 600,
              cursor: updatingPassword ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Save size={16} />
            {updatingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DriverProfile;
